/*
 * MCRO001-F0050 / T-MCRO-050 (design: choreo MCRO001-D004 §4.1.3)
 * Slice 2 of 3 — id minting for annotation nodes.
 *
 * Slice A (the lexer) deliberately leaves parsed nodes with an EMPTY id, keeping the
 * lexer PURE (no rng, no clock). Id generation lives HERE, in the presentation/producer
 * layer, so a node only acquires a stable identity when it is first bound to a picker.
 *
 * This is a small, self-contained UUIDv7 generator with NO choreography coupling. It
 * mirrors the proven collaborationProtocol.uuidV7 layout so ids sort by creation time,
 * but is kept in src/lib/annotation to avoid dragging the choreography protocol module
 * into the generic annotation components.
 */

const UUID_V7_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-7[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Generate a time-ordered UUIDv7. Randomness comes from the platform CSPRNG; the first
 * 48 bits are the millisecond timestamp so ids are monotonic-ish and sortable.
 *
 * @param {number} [now]                 Epoch milliseconds (injectable for tests).
 * @param {Crypto} [cryptoObject]        The crypto provider (injectable for tests).
 * @returns {string} a canonical UUIDv7 string.
 */
export function uuidv7(now = Date.now(), cryptoObject = globalThis.crypto) {
  if (!cryptoObject || typeof cryptoObject.getRandomValues !== 'function') {
    throw new Error('Secure randomness is required to mint an annotation id.');
  }
  const bytes = new Uint8Array(16);
  cryptoObject.getRandomValues(bytes);
  let timestamp = BigInt(now);
  for (let index = 5; index >= 0; index -= 1) {
    bytes[index] = Number(timestamp & 0xffn);
    timestamp >>= 8n;
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x70; // version 7
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variant 10xx
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** True iff `value` is a syntactically valid UUIDv7 string. */
export function isUuidV7(value) {
  return typeof value === 'string' && UUID_V7_RE.test(value);
}

/**
 * Return a node id, minting a fresh UUIDv7 when the node lacks one. A node that already
 * carries a (non-empty) id keeps it — ids are stable once assigned. Does NOT mutate the
 * input node.
 *
 * @param {{ id?: string }} node
 * @param {() => string} [mint]  id generator (injectable for tests).
 * @returns {string}
 */
export function ensureNodeId(node, mint = uuidv7) {
  const existing = node && typeof node.id === 'string' ? node.id : '';
  return existing !== '' ? existing : mint();
}
